import { CalendarOutlined, MenuOutlined, UserOutlined } from "@ant-design/icons"
import { type MenuItemType } from "components/Menu/Menu"
import { BaseLayout } from "components/BaseLayout/BaseLayout"
import { Empty } from "components/Empty/Empty"
import { useSpotlight } from "hooks/useSpotlight"
import { UnauthorizedIcon } from "icons/UnauthorizedIcon"
import { ScopeSelect } from "modules/scope/ScopeSelect"
import { useSupportRequests } from "modules/support/useSupportRequests"
import { useAuthorizedRoutes } from "pages/router"
import { useMemo, useRef } from "react"
import { useOutlet, useParams } from "react-router"
import { useUserRole } from "services/users"
import { cn } from "styles/cn"

export function VisitsLayout() {
	const { scopeId } = useParams()
	const { allowedRoutes } = useAuthorizedRoutes()
	const { isFocused } = useSpotlight()
	const { data: { fullName, fieldSupportTeams = [] } = {}, isFetched } =
		useUserRole()
	const currentOutlet = useOutlet()
	const previousOutlet = useRef<React.ReactNode>(null)
	const isModalPath = location.pathname.includes("/m/")
	const supportRequests = useSupportRequests()
	const items = useMemo<MenuItemType[]>(
		() => [
			...(allowedRoutes.includes("/:scopeId/visits")
				? [
						{
							key: `/${scopeId}/visits`,
							label: "My visits",
							icon: <UserOutlined aria-hidden />,
						},
					]
				: []),
			...(allowedRoutes.includes("/:scopeId/visits/all")
				? [
						{
							key: `/${scopeId}/visits/all`,
							label: "All visits",
							icon: <CalendarOutlined aria-hidden />,
							className: cn(
								isFocused("all-visits-tab") && "animate-pulse bg-green-20",
							),
						},
					]
				: []),
			...(allowedRoutes.includes("/:scopeId/visits/unscheduled")
				? [
						{
							key: `/${scopeId}/visits/unscheduled`,
							label: "Needs scheduling",
							icon: <MenuOutlined aria-hidden />,
							className: cn(
								isFocused("needs-scheduling-tab") &&
									"animate-pulse bg-green-20",
							),
						},
					]
				: []),
		],
		[allowedRoutes, isFocused, scopeId],
	)

	// Persist the previous non-modal outlet
	if (!isModalPath) {
		previousOutlet.current = currentOutlet
	}

	return (
		<BaseLayout className={cn(items.length === 0 && "lg:grid-cols-1")}>
			<BaseLayout.Header
				username={fullName}
				items={items}
				supportRequestsItems={supportRequests.items}
				supportRequestsUpdatesCount={supportRequests.ownUpdatesCount}
			>
				<ScopeSelect basePath="/visits" ariaLabel="Select a team" />
			</BaseLayout.Header>

			{items.length > 0 && (
				<BaseLayout.SideNav
					items={items}
					supportRequestsItems={supportRequests.items}
					supportRequestsUpdatesCount={supportRequests.ownUpdatesCount}
				/>
			)}

			<BaseLayout.Main>
				{fieldSupportTeams.length === 0 && isFetched ? (
					<Empty
						Icon={UnauthorizedIcon}
						title="No field support teams found"
						description="Your account is not currently associated with any field support team. Please
													contact Empo Health to be added to a team."
					/>
				) : (
					<>
						{/* Background Layer: Either the previous page or current page */}
						{isModalPath ? previousOutlet.current : currentOutlet}

						{/* Foreground Modal Layer: Only when on the modal path */}
						{isModalPath && currentOutlet}
					</>
				)}
			</BaseLayout.Main>
		</BaseLayout>
	)
}
